import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Activity,
  Loader2,
  User,
  Calendar,
  Search,
  Download,
  ChevronDown,
  Users,
  Check,
  CalendarRange,
  Smartphone,
  HardDrive,
  AlertCircle,
  FileText,
  Briefcase,
  Monitor,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "../hooks/useToast";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

import api from "../api/api";
import { getEmployees } from "../api/employees";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface EmployeeRecord {
  employeeID: string;
  employeeName: string;
  work_Start: string | null;
  work_end: string | null;
  date: string;
  WorkFromHome: boolean;
  in_time: {
    attendance: string | null;
    work_monitor: string | null;
    work_activity: string | null;
    difference_minutes: number;
  };
  out_time: {
    attendance: string | null;
    work_monitor: string | null;
    work_activity: string | null;
    difference_minutes: number;
  };
  idle_time: {
    attendance_hours: number;
    work_monitor_hours: number;
    work_activity_hours: number;
    difference_minutes: number;
  };
  status: {
    present: boolean;
    absent: boolean;
    leave: boolean;
    work_from_home: boolean;
  };
  leave: {
    present: boolean;
    absent: boolean;
    leave: boolean;
    work_from_home: boolean;
  };
}

interface AppUpdate {
  id: string;
  name: string;
  version: string;
  size: string;
  updatedDate: string;
  downloadUrl: string;
}

interface Employee {
  employeeID: string;
  employee_name?: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
  id?: string;
}

type StatusFilterType = "All" | "Present" | "Absent" | "Leave" | "WFH";

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtTime = (t: string | null): string => {
  if (!t) return "—";
  const raw = t.includes(" ") ? t.split(" ")[1] : t;
  const parts = raw.split(":");
  return `${parts[0]}:${parts[1]}`;
};

const fmtHours = (h: number): string => {
  if (h === 0) return "—";
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
};

const fmtDate = (d: string): string => {
  try {
    return format(new Date(d), "dd MMM yyyy");
  } catch {
    return d;
  }
};

const getOverallStatus = (s: EmployeeRecord["status"]): StatusFilterType => {
  if (s.absent) return "Absent";
  if (s.leave) return "Leave";
  if (s.work_from_home) return "WFH";
  if (s.present) return "Present";
  return "All";
};

// Normalize raw API response (array or wrapped object) to EmployeeRecord[]
const normalizeReport = (raw: unknown): EmployeeRecord[] => {
  if (Array.isArray(raw)) return raw as EmployeeRecord[];
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.report)) return obj.report as EmployeeRecord[];
  }
  return [];
};

// ─── AppUpdateCard ──────────────────────────────────────────────────────────────

const AppUpdateCard: React.FC<{ app: AppUpdate }> = ({ app }) => {
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 1500));
      const a = document.createElement("a");
      a.href = app.downloadUrl;
      a.download = `${app.name}_${app.version}.exe`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast({
        title: "Download Started",
        description: `${app.name} ${app.version}`,
      });
    } catch {
      toast({
        title: "Download Failed",
        description: "Try again later.",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  };

  const Icon =
    app.name.toLowerCase().includes("hr") ||
    app.name.toLowerCase().includes("payroll")
      ? Briefcase
      : app.name.toLowerCase().includes("work") ||
          app.name.toLowerCase().includes("monitor")
        ? Monitor
        : Smartphone;

  return (
    <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 rounded-lg">
            <Icon className="h-7 w-7 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-slate-800 truncate">
                {app.name}
              </p>
              <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-0 shrink-0">
                Latest
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
              <span>v{app.version}</span>
              <span>•</span>
              <HardDrive className="h-2.5 w-2.5" />
              <span>{app.size}</span>
              <span>•</span>
              <Calendar className="h-2.5 w-2.5" />
              <span>{app.updatedDate}</span>
            </div>
          </div>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            size="sm"
            className="h-7 px-3 text-[10px] bg-slate-800 hover:bg-slate-700 shrink-0"
          >
            {downloading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Download className="h-3 w-3" />
            )}
            <span className="ml-1">{downloading ? "..." : "Download"}</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Table Cell Components ──────────────────────────────────────────────────────

const StatusCell: React.FC<{ value: boolean | null; na?: boolean }> = ({
  value,
  na,
}) => {
  if (na) return <span className="text-slate-400 text-[10px]">NA</span>;
  return value ? (
    <span className="text-emerald-600 font-semibold text-[10px]">Yes</span>
  ) : (
    <span className="text-slate-400 text-[10px]">No</span>
  );
};

const DiffCell: React.FC<{ mins: number; na?: boolean }> = ({ mins, na }) => {
  if (na) return <span className="text-slate-400 text-[10px]">NA</span>;
  const color =
    mins === 0
      ? "text-slate-500"
      : mins <= 5
        ? "text-amber-600"
        : "text-red-600";
  return (
    <span className={`text-[10px] font-medium ${color}`}>
      {mins === 0 ? "0 min" : `${mins} min`}
    </span>
  );
};

// ─── EmployeeTableCard ──────────────────────────────────────────────────────────

const EmployeeTableCard: React.FC<{ emp: EmployeeRecord }> = ({ emp }) => {
  const status = getOverallStatus(emp.status);
  const statusColor =
    status === "Present"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "Absent"
        ? "bg-red-50 text-red-700 border-red-200"
        : status === "Leave"
          ? "bg-blue-50 text-blue-700 border-blue-200"
          : status === "WFH"
            ? "bg-purple-50 text-purple-700 border-purple-200"
            : "bg-slate-50 text-slate-600 border-slate-200";

  const rows = [
    {
      label: "In Time",
      attendance: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.in_time.attendance)}
        </span>
      ),
      monitor: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.in_time.work_monitor)}
        </span>
      ),
      activity: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.in_time.work_activity)}
        </span>
      ),
      diff: <DiffCell mins={emp.in_time.difference_minutes} />,
    },
    {
      label: "Out Time",
      attendance: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.out_time.attendance)}
        </span>
      ),
      monitor: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.out_time.work_monitor)}
        </span>
      ),
      activity: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.out_time.work_activity)}
        </span>
      ),
      diff: <DiffCell mins={emp.out_time.difference_minutes} />,
    },
    {
      label: "Idle Time",
      attendance: (
        <span className="text-[10px] text-slate-700">
          {fmtHours(emp.idle_time.attendance_hours)}
        </span>
      ),
      monitor: (
        <span className="text-[10px] text-slate-700">
          {fmtHours(emp.idle_time.work_monitor_hours)}
        </span>
      ),
      activity: (
        <span className="text-[10px] text-slate-700">
          {fmtHours(emp.idle_time.work_activity_hours)}
        </span>
      ),
      diff: <DiffCell mins={emp.idle_time.difference_minutes} />,
    },
    {
      label: "Present",
      attendance: <StatusCell value={emp.status.present} />,
      monitor: <StatusCell value={emp.status.present} />,
      activity: <StatusCell value={emp.status.present} />,
      diff: <StatusCell na />,
    },
    {
      label: "Absent",
      attendance: <StatusCell value={emp.status.absent} />,
      monitor: <StatusCell value={emp.status.absent} />,
      activity: <StatusCell value={emp.status.absent} />,
      diff: <StatusCell na />,
    },
    {
      label: "Leave",
      attendance: <StatusCell value={emp.leave.leave} />,
      monitor: <StatusCell value={emp.leave.leave} />,
      activity: <StatusCell value={emp.leave.leave} />,
      diff: <StatusCell na />,
    },
    {
      label: "Work From Home",
      attendance: <StatusCell value={emp.status.work_from_home} />,
      monitor: <StatusCell value={emp.status.work_from_home} />,
      activity: <StatusCell value={emp.status.work_from_home} />,
      diff: <StatusCell na />,
    },
  ];

  return (
    <Card className="border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center">
            <User className="h-3 w-3 text-slate-600" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-800">
              {emp.employeeName}
            </p>
            <p className="text-[9px] text-slate-500">
              {emp.employeeID}&nbsp;•&nbsp;{fmtDate(emp.date)}
            </p>
          </div>
        </div>
        <Badge className={`text-[9px] px-2 h-5 border ${statusColor}`}>
          {status}
        </Badge>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-700 text-white">
              <th className="px-3 py-1.5 text-[10px] font-semibold w-28">
                Particulars
              </th>
              <th className="px-3 py-1.5 text-[10px] font-semibold text-center">
                Attendance
              </th>

              <th className="px-3 py-1.5 text-[10px] font-semibold text-center">
                Work Activity
              </th>
              <th className="px-3 py-1.5 text-[10px] font-semibold text-center">
                Diff (Activity vs Attendance)
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.label}
                className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}
              >
                <td className="px-3 py-1.5 text-[10px] font-medium text-slate-600 border-r border-slate-100">
                  {row.label}
                </td>
                <td className="px-3 py-1.5 text-center border-r border-slate-100">
                  {row.attendance}
                </td>

                <td className="px-3 py-1.5 text-center border-r border-slate-100">
                  {row.activity}
                </td>
                <td className="px-3 py-1.5 text-center">{row.diff}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────────────────────

export const AWADashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"daily" | "individual">("daily");

  // ── Daily state ──
  // allDailyRecords = full unfiltered source of truth from API
  const [allDailyRecords, setAllDailyRecords] = useState<EmployeeRecord[]>([]);
  const [dailyMeta, setDailyMeta] = useState<{
    work_date: string;
    total_employees: number;
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), "yyyy-MM-dd"),
  );

  // ── Individual state ──
  const [individualRecords, setIndividualRecords] = useState<EmployeeRecord[]>(
    [],
  );
  const [individualMeta, setIndividualMeta] = useState<{
    employeeID: string;
    employeeName: string;
    start_date: string;
    end_date: string;
  } | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // ── Common state ──
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [openEmpDropdown, setOpenEmpDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>("All");
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const appUpdates: AppUpdate[] = [
    {
      id: "1",
      name: "Teamlio",
      version: "3.2.1",
      size: "10 MB",
      updatedDate: "15/01/2026",
      downloadUrl: "https://api-hr.startupkhata.com/download_hr_app/hr_payroll",
    },
    {
      id: "2",
      name: "Work Monitor",
      version: "2.5.0",
      size: "136 MB",
      updatedDate: "24/03/2026",
      downloadUrl:
        "https://api-hr.startupkhata.com/download_hr_app/work_monitor",
    },
  ];

  // Fetch employees on mount
  useEffect(() => {
    (async () => {
      setLoadingEmployees(true);
      try {
        const res = await getEmployees();
        setEmployees(res?.employees || []);
      } catch {
        toast({
          title: "Warning",
          description: "Could not fetch employees.",
          variant: "destructive",
        });
      } finally {
        setLoadingEmployees(false);
      }
    })();
  }, []);

  // ── Fetch daily report ─────────────────────────────────────────────────────
  const fetchDailyReport = async () => {
    if (!selectedDate) {
      setError("Please select a date");
      return;
    }
    setLoading(true);
    setError(null);
    // Reset search/filter whenever new data is loaded
    setSearchQuery("");
    setStatusFilter("All");

    try {
      const res = await api.get("/awa-report", {
        params: { work_date: selectedDate },
      });
      const records = normalizeReport(res.data);

      if (records.length > 0) {
        setAllDailyRecords(records);

        // Build meta
        if (!Array.isArray(res.data) && res.data?.work_date) {
          setDailyMeta({
            work_date: res.data.work_date,
            total_employees: res.data.total_employees ?? records.length,
          });
        } else {
          setDailyMeta({
            work_date: records[0]?.date || selectedDate,
            total_employees: records.length,
          });
        }

        toast({
          title: "Success",
          description: `Report loaded for ${fmtDate(selectedDate)}`,
        });
      } else {
        setAllDailyRecords([]);
        setDailyMeta(null);
        setError(`No data found for ${fmtDate(selectedDate)}`);
        toast({
          title: "No Data",
          description: "No records for selected date.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("fetchDailyReport:", err);
      setError("Failed to fetch report. Please check your connection.");
      toast({
        title: "Error",
        description: "Failed to fetch report.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch individual report ────────────────────────────────────────────────
  const fetchIndividualReport = async () => {
    if (!selectedEmployee) {
      toast({
        title: "Error",
        description: "Select an employee.",
        variant: "destructive",
      });
      return;
    }
    if (startDate > endDate) {
      toast({
        title: "Error",
        description: "Start date must be before end date.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const res = await api.get("/awa-report/employee", {
        params: {
          employee_id: selectedEmployee.id || selectedEmployee.employeeID,
          start_date: startDate,
          end_date: endDate,
        },
      });

      const records = normalizeReport(res.data);

      if (records.length > 0) {
        setIndividualRecords(records);

        let empName =
          `${selectedEmployee.first_name || ""} ${selectedEmployee.last_name || ""}`.trim();
        let empID = selectedEmployee.employeeID;

        if (!Array.isArray(res.data) && res.data?.employeeName) {
          empName = res.data.employeeName;
          empID = res.data.employeeID || empID;
        } else if (records[0]?.employeeName) {
          empName = records[0].employeeName;
          empID = records[0].employeeID || empID;
        }

        setIndividualMeta({
          employeeID: empID,
          employeeName: empName,
          start_date: startDate,
          end_date: endDate,
        });
        toast({
          title: "Success",
          description: `Report loaded for ${empName}`,
        });
      } else {
        setIndividualRecords([]);
        setIndividualMeta(null);
        setError("No data found for selected employee and date range.");
        toast({
          title: "No Data",
          description: "No records found.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("fetchIndividualReport:", err);
      setError("Failed to fetch employee report.");
      toast({
        title: "Error",
        description: "Failed to fetch employee report.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-load daily report on date / tab change
  useEffect(() => {
    if (activeTab === "daily") fetchDailyReport();
  }, [selectedDate, activeTab]);

  // ── filteredDaily: computed from allDailyRecords + search + statusFilter ──
  const filteredDaily = useMemo(() => {
    let list = allDailyRecords;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (e) =>
          e.employeeName.toLowerCase().includes(q) ||
          e.employeeID.toLowerCase().includes(q),
      );
    }

    if (statusFilter !== "All") {
      list = list.filter((e) => getOverallStatus(e.status) === statusFilter);
    }

    return list;
  }, [allDailyRecords, searchQuery, statusFilter]);

  // ── Summary stats — always from full unfiltered list ──
  const summaryStats = useMemo(
    () => ({
      present: allDailyRecords.filter(
        (e) => e.status.present && !e.status.work_from_home,
      ).length,
      absent: allDailyRecords.filter((e) => e.status.absent).length,
      leave: allDailyRecords.filter((e) => e.status.leave).length,
      wfh: allDailyRecords.filter((e) => e.status.work_from_home).length,
    }),
    [allDailyRecords],
  );

  // ── Employee dropdown search ──
  const filteredEmployees = useMemo(() => {
    if (!employeeSearch) return employees;
    const q = employeeSearch.toLowerCase();
    return employees.filter(
      (e) =>
        e.first_name?.toLowerCase().includes(q) ||
        e.last_name?.toLowerCase().includes(q) ||
        e.employeeID.toLowerCase().includes(q),
    );
  }, [employees, employeeSearch]);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--gt-surface-1)" }}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-20"
        style={{
          background: "var(--gt-surface-0)",
          borderBottom: "1px solid var(--gt-border)",
          boxShadow: "var(--gt-elev-1)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div
              className="flex items-center justify-center"
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: "var(--gt-primary)",
              }}
            >
              <Activity className="h-4 w-4" style={{ color: "var(--gt-on-primary)" }} />
            </div>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontFamily: "var(--gt-font-display)",
                  fontSize: 22,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "var(--gt-text-primary)",
                  lineHeight: 1.1,
                }}
              >
                AWA Report
              </h1>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--gt-text-tertiary)",
                  marginTop: 2,
                  fontWeight: 500,
                }}
              >
                Attendance &amp; Work Activity
              </p>
            </div>
          </div>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as "daily" | "individual")}
          >
            <TabsList className="h-7 p-0.5">
              <TabsTrigger value="daily" className="text-[10px] px-3 h-6">
                Daily Report
              </TabsTrigger>
              <TabsTrigger value="individual" className="text-[10px] px-3 h-6">
                Employee Report
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {/* App Updates */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Download className="h-3.5 w-3.5 text-slate-500" /> App Updates
            </p>
            <Badge variant="outline" className="text-[9px]">
              {appUpdates.length} updates
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {appUpdates.map((a) => (
              <AppUpdateCard key={a.id} app={a} />
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
          </div>
        )}

        {/* ══ DAILY TAB ══ */}
        {activeTab === "daily" && (
          <>
            {/* Date picker */}
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40 h-8 text-xs bg-white"
              />
              <Button
                onClick={fetchDailyReport}
                disabled={loading}
                size="sm"
                className="h-8 px-4 text-xs bg-slate-800 hover:bg-slate-700"
              >
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Search className="h-3 w-3 mr-1" />
                )}
                Load
              </Button>
            </div>

            {allDailyRecords.length > 0 && dailyMeta && (
              <>
                {/* Info bar */}
                <div className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
                    <CalendarRange className="h-3 w-3" />
                    <span className="font-medium">
                      {fmtDate(dailyMeta.work_date)}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500">
                    Total: {dailyMeta.total_employees} employees
                  </span>
                </div>

                {/* Summary stats (always from full unfiltered data) */}
                <div className="grid grid-cols-4 gap-2">
                  {[
                    {
                      label: "Present",
                      value: summaryStats.present,
                      color: "bg-emerald-50 text-emerald-700",
                    },
                    {
                      label: "Absent",
                      value: summaryStats.absent,
                      color: "bg-red-50 text-red-700",
                    },
                    {
                      label: "Leave",
                      value: summaryStats.leave,
                      color: "bg-blue-50 text-blue-700",
                    },
                    {
                      label: "WFH",
                      value: summaryStats.wfh,
                      color: "bg-purple-50 text-purple-700",
                    },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className={`rounded-lg p-2 text-center ${s.color}`}
                    >
                      <p className="text-[9px] font-medium uppercase tracking-wide">
                        {s.label}
                      </p>
                      <p className="text-base font-bold">{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Search + Filter */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                    <Input
                      placeholder="Search by name or employee ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-7 h-8 text-xs bg-white"
                    />
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {(
                      [
                        "All",
                        "Present",
                        "Absent",
                        "Leave",
                        "WFH",
                      ] as StatusFilterType[]
                    ).map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={statusFilter === s ? "default" : "outline"}
                        onClick={() => setStatusFilter(s)}
                        className={`h-7 px-2.5 text-[10px] ${statusFilter === s ? "bg-slate-800 text-white" : ""}`}
                      >
                        {s}
                        {s !== "All" && (
                          <span className="ml-1 opacity-60">
                            (
                            {
                              allDailyRecords.filter(
                                (e) => getOverallStatus(e.status) === s,
                              ).length
                            }
                            )
                          </span>
                        )}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Showing count */}
                {(searchQuery.trim() || statusFilter !== "All") && (
                  <p className="text-[10px] text-slate-500">
                    Showing {filteredDaily.length} of {allDailyRecords.length}{" "}
                    employees
                  </p>
                )}

                {/* Cards */}
                {filteredDaily.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">
                    No employees match your search or filter.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredDaily.map((emp) => (
                      <EmployeeTableCard key={emp.employeeID} emp={emp} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ══ INDIVIDUAL TAB ══ */}
        {activeTab === "individual" && (
          <>
            <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
              {/* Employee picker */}
              <Popover open={openEmpDropdown} onOpenChange={setOpenEmpDropdown}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="justify-between h-8 text-xs bg-white flex-1 max-w-xs"
                  >
                    {selectedEmployee ? (
                      <span className="truncate">
                        {selectedEmployee.first_name}{" "}
                        {selectedEmployee.last_name}
                      </span>
                    ) : (
                      <span className="text-slate-400">Select employee...</span>
                    )}
                    <ChevronDown className="ml-1 h-3 w-3 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search employee..."
                      value={employeeSearch}
                      onValueChange={setEmployeeSearch}
                      className="h-8 text-xs"
                    />
                    <CommandList>
                      <CommandEmpty className="text-xs py-2">
                        {loadingEmployees ? "Loading..." : "No employee found."}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredEmployees.map((emp) => (
                          <CommandItem
                            key={emp.employeeID}
                            value={`${emp.first_name} ${emp.last_name} ${emp.employeeID}`}
                            onSelect={() => {
                              setSelectedEmployee(emp);
                              setEmployeeSearch("");
                              setOpenEmpDropdown(false);
                            }}
                            className="cursor-pointer text-xs"
                          >
                            <Check
                              className={`mr-2 h-3 w-3 ${
                                selectedEmployee?.employeeID === emp.employeeID
                                  ? "opacity-100"
                                  : "opacity-0"
                              }`}
                            />
                            <div>
                              <p className="text-xs font-medium">
                                {emp.first_name} {emp.last_name}
                              </p>
                              <p className="text-[9px] text-slate-500">
                                {emp.employeeID}
                              </p>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36 h-8 text-xs bg-white"
              />
              <span className="self-center text-xs text-slate-400">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36 h-8 text-xs bg-white"
              />
              <Button
                onClick={fetchIndividualReport}
                disabled={loading || !selectedEmployee}
                size="sm"
                className="h-8 px-4 text-xs bg-slate-800 hover:bg-slate-700"
              >
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Search className="h-3 w-3 mr-1" />
                )}
                Load
              </Button>
            </div>

            {!selectedEmployee && !loading && (
              <div className="text-center py-12">
                <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  Select an employee to view their report
                </p>
              </div>
            )}

            {individualMeta && individualRecords.length > 0 && (
              <>
                <div className="p-3 bg-slate-800 rounded-lg text-white flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold">
                      {individualMeta.employeeName}
                    </p>
                    <p className="text-[10px] opacity-70">
                      {individualMeta.employeeID}
                    </p>
                  </div>
                  <div className="text-right text-[10px] opacity-80">
                    <p>
                      {fmtDate(individualMeta.start_date)} →{" "}
                      {fmtDate(individualMeta.end_date)}
                    </p>
                    <p>{individualRecords.length} day(s)</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    Daily Breakdown ({individualRecords.length} record(s))
                  </p>
                  {individualRecords.map((rec, i) => (
                    <EmployeeTableCard
                      key={`${rec.employeeID}-${i}`}
                      emp={rec}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Loader */}
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          </div>
        )}
      </div>
    </div>
  );
};

export default AWADashboard;
